import cn from "classnames";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { payableStatuses } from "components/distributionBids/paymentFields";
import { accountsReceivableReportSourcesEnum, bidPaymentStatuses, bidPaymentStatusesEnum } from "helpers/enums";
import { ending } from "helpers/i18n";
import { paths } from "helpers/paths";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import { columnName, formatValue, typeSpecificProps } from "helpers/reports";
import { alphabeticalSort, enumLabel, formatNumber, ymEvent } from "helpers/system";
import isEqual from "lodash/isEqual";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React from "react";
import { Link } from "react-router-dom";
import { getItems as cargoOwnersInfo, namesList as counterpartiesList } from "sources/books/cargoOwners";
import { index as distributionBidsList } from "sources/books/distributionBids";
import { listForSelect as hiredCarsListForSelect } from "sources/books/hiredCars";
import { list as legalPersons } from "sources/books/legalPersons";
import { numbersListWithDeleted } from "sources/books/trucks";
import CommonReport from "../commonReport";
import CounterpartyInfo from "./counterpartyInfo";
import WithChangeDate from "./withChangeDate";
import WithChangeStatus from "./withChangeStatus";

const NO_PLAN_PAYMENT_DATE_LABEL = "Без даты оплаты";

const isEmptyPlanPaymentDate = (value) =>
    value === null || value === undefined || (typeof value === "string" && value.trim() === "");

const normalizePlanPaymentDate = (row) => ({
    ...row,
    planPaymentDate: isEmptyPlanPaymentDate(row.planPaymentDate) ? null : row.planPaymentDate,
});

const planPaymentDateGroupLabel = (value) => {
    if (isEmptyPlanPaymentDate(value)) {
        return NO_PLAN_PAYMENT_DATE_LABEL;
    }

    const date = moment(value, "YYYY-MM-DD");
    return date.isValid() ? date.format("DD.MM.YYYY") : value;
};

export default class AccountsReceivableReport extends CommonReport {
    state = {
        filters: [],
        expanded: [],
        sortBy: [],
        tableDefaultProps: { columns: [] },
        data: [],
        visibleRows: null,
        visibleRowsSignature: "",
    };

    componentDidMount() {
        this.setReportData(this.props.data.data);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.dataLoadedAt !== this.props.dataLoadedAt) {
            this.setReportData(this.props.data.data);
        }
    }

    setReportData = (data) => {
        const formattedData = this.formatData(data);
        this.setState({
            tableDefaultProps: this.tableDefaultProps(),
            data: formattedData,
            visibleRows: null,
            visibleRowsSignature: "",
        });
        const requestId = (this.reportEnrichmentRequestId || 0) + 1;
        this.reportEnrichmentRequestId = requestId;
        const counterpartyIds = [...new Set(formattedData.map((row) => row.counterpartyId).filter(Boolean))];
        if (counterpartyIds.length > 0) {
            cargoOwnersInfo({ id: { in: counterpartyIds } }).then((counterparties) => {
                if (this.reportEnrichmentRequestId !== requestId || !counterparties) {
                    return;
                }
                const counterpartiesById = counterparties.reduce((result, counterparty) => {
                    result[counterparty.id] = counterparty;
                    return result;
                }, {});
                this.setState((state) => ({
                    data: state.data.map((row) => ({
                        ...row,
                        counterpartyInn: counterpartiesById[row.counterpartyId]?.inn || "",
                    })),
                }));
            });
        }
        const distributionBidIds = [
            ...new Set(
                formattedData
                    .filter((row) => row.isDistributionBid)
                    .map((row) => row.bidId)
                    .filter(Boolean),
            ),
        ];
        if (distributionBidIds.length > 0) {
            distributionBidsList({
                preparedFilter: { id: { in: distributionBidIds } },
                start: 0,
                length: distributionBidIds.length,
            }).then((response) => {
                if (this.reportEnrichmentRequestId !== requestId || !response) {
                    return;
                }
                const orders = response.data || response;
                const ordersById = orders.reduce((result, order) => {
                    result[order.id] = order;
                    return result;
                }, {});
                this.setState((state) => ({
                    data: state.data.map((row) => ({
                        ...row,
                        orderDate: row.isDistributionBid ? ordersById[row.bidId]?.createdAt : undefined,
                    })),
                }));
            });
        }
    };

    formatData = (data) => {
        if (this.props.groupByDates) {
            const result = [];
            data.forEach((row) => {
                const { bids, ...rest } = row;
                if (bids?.length > 0) {
                    bids.forEach((bidRow) => {
                        result.push(normalizePlanPaymentDate({ ...bidRow, ...rest }));
                    });
                } else {
                    result.push(normalizePlanPaymentDate({ ...rest, isEmpty: true }));
                }
            });
            return result;
        }
        return data.map(normalizePlanPaymentDate);
    };

    updateData = (newItem, index) => {
        const newData = [...this.state.data];
        newData[index] = newItem;
        this.setState({ data: newData });
    };

    setVisibleRows = (visibleRows) => {
        const visibleRowsSignature = JSON.stringify(
            visibleRows.map((row) => [
                row.bidId,
                row.bidPaymentStatus,
                row.price,
                row.remainingPayment,
                row.planPaymentDate,
                row.invoiceDate,
                row.isPaymentOverdue,
            ]),
        );
        if (visibleRowsSignature !== this.state.visibleRowsSignature) {
            this.setState({ visibleRows, visibleRowsSignature });
        }
    };

    backendFilterValue = (filters, id) => filters.find((filter) => filter.id === id)?.value?.value;

    backendParamsFromFilters = (filters) => {
        const counterpartyId = this.backendFilterValue(filters, "counterpartyName");
        const carId = this.backendFilterValue(filters, "carNumber");
        const paymentStatus = this.backendFilterValue(filters, "bidPaymentStatus");

        return {
            ...(counterpartyId ? { counterpartyId } : {}),
            ...(carId && !this.isMixedSource() ? { carId } : {}),
            ...(paymentStatus?.length ? { paymentStatus } : {}),
        };
    };

    setTableState = (data) => {
        const filterIds = [
            "counterpartyName",
            "bidPaymentStatus",
            ...(this.isMixedSource() ? [] : ["carNumber"]),
        ];
        const backendFilterChanged = filterIds.some((id) => {
            return !isEqual(this.backendFilterValue(this.state.filters, id), this.backendFilterValue(data.filters, id));
        });
        if (backendFilterChanged && !this.props.isPrintForm) {
            this.props.getItem(this.backendParamsFromFilters(data.filters));
        }
        this.setState(data);
    };

    isHiredCarSource = () =>
        this.props.isAccountsPayable || this.props.source === accountsReceivableReportSourcesEnum.HiredCar;

    isMixedSource = () =>
        !this.props.isAccountsPayable && this.props.source === accountsReceivableReportSourcesEnum.All;

    paymentStatuses = () => (this.props.isAccountsPayable ? payableStatuses : bidPaymentStatuses);

    paymentStatusClassName = (payment) => {
        if (payment.bidPaymentStatus === bidPaymentStatusesEnum.Paid) {
            return "badge-success";
        }
        if (payment.overdueDays > 1) {
            return "badge-danger";
        }
        const planDate = moment(payment.planPaymentDate, "YYYY-MM-DD", true);
        const daysUntilPayment = planDate.isValid() ? planDate.diff(moment().startOf("day"), "days") : null;
        if (daysUntilPayment !== null && daysUntilPayment >= 0 && daysUntilPayment <= 1) {
            return "badge-warning";
        }
        return "badge-secondary";
    };

    entityLink = (row) => {
        const isDistributionBid = row.original.isDistributionBid;
        const value = row.original.bidId;
        const prefix = this.isMixedSource() ? `${isDistributionBid ? "Заказ" : "Заявка"} № ` : "";
        const path = isDistributionBid ? paths.distributionBidShow(value) : paths.bidShow("bid", value);
        return (
            <Link to={path}>
                {prefix}
                {value}
            </Link>
        );
    };

    extractColumns = () => {
        return [
            {
                Header: this.isMixedSource() ? "Основание" : this.isHiredCarSource() ? "Номер заказа" : "Номер заявки",
                accessor: "bidId",
                aggregate: "count",
                Aggregated: ({ value, row }) => {
                    if (row.isGrouped) {
                        value = row.subRows[0].original.bidsCount;
                    }
                    return (
                        <div className="text-center">
                            <b>{formatValue(value, "number")}</b>
                        </div>
                    );
                },
                Cell: ({ value, row }) => (
                    <div className="text-center">{row.isGrouped ? <b>{value}</b> : this.entityLink(row)}</div>
                ),
                ...typeSpecificProps({ clrType: "string" }),
            },
            {
                Header: "Контрагент",
                accessor: "counterpartyName",
                Cell: ({ value }) => (
                    <div className="long-text" data-test-id="counterpartyName">
                        {value}
                    </div>
                ),
                filter: (rows, _id, filterValue) => {
                    if (!filterValue || !filterValue?.value) {
                        return rows;
                    }
                    const value = filterValue.value;
                    return rows.filter((row) => row.values.counterpartyId === value);
                },
                Filter: reportFilterWrapper((props) => {
                    return (
                        <SelectColumnFilter
                            {...props}
                            onFilteredChange={(payload) => {
                                if (payload.value) {
                                    ymEvent("FilteringСounterpartiesReceivable");
                                }
                                props.onFilteredChange(payload);
                            }}
                            selectProps={{
                                url: counterpartiesList,
                                inputProps: { name: "counterpartyId" },
                                propName: "name",
                            }}
                        />
                    );
                }),
                sortType: (rowA, rowB) => alphabeticalSort(rowA.values, rowB.values, "counterpartyName"),
            },
            {
                Header: "Организация",
                accessor: "legalPersonName",
                Cell: ({ value }) => (
                    <div className="long-text" data-test-id="legalPerson">
                        {value}
                    </div>
                ),
                filter: (rows, _id, filterValue) => {
                    if (!filterValue || !filterValue?.value) {
                        return rows;
                    }
                    const value = filterValue.value;
                    return rows.filter((row) => row.values.legalPersonId === value);
                },
                Filter: reportFilterWrapper((props) => (
                    <SelectColumnFilter
                        {...props}
                        onFilteredChange={(payload) => props.onFilteredChange(payload)}
                        selectProps={{
                            url: legalPersons,
                            inputProps: { name: "legalPersonId" },
                            propName: "name",
                        }}
                    />
                )),
                sortType: (rowA, rowB) => alphabeticalSort(rowA.values, rowB.values, "legalPersonName"),
            },
            {
                Header: "ИНН контрагента",
                accessor: "counterpartyInn",
                Cell: ({ value }) => <div className="text-center">{value}</div>,
                ...typeSpecificProps({ clrType: "string" }),
            },
            {
                Header: this.props.isAccountsPayable
                    ? "Сумма к выплате"
                    : this.isMixedSource()
                      ? "Сумма"
                      : this.isHiredCarSource()
                        ? "Стоимость заказа"
                        : "Стоимость всех заявок",
                accessor: "price",
                Aggregated: ({ value, row }) => {
                    if (row.isGrouped) {
                        value = row.subRows[0].original.planIncome;
                    }
                    return (
                        <div className="text-right">
                            <b>{formatValue(value, "double")}</b>
                        </div>
                    );
                },
                Cell: ({ value }) => <div className="text-right">{formatValue(value, "double")}</div>,
                ...typeSpecificProps({ clrType: "double" }),
            },
            {
                Header: "Полученные платежи",
                accessor: "receivedAmountByDate",
                Aggregated: ({ value, row }) => {
                    if (row.isGrouped) {
                        value = row.subRows[0].original.receivedAmountByDate;
                    }
                    return (
                        <div className="text-right">
                            <b>{formatValue(value, "double")}</b>
                        </div>
                    );
                },
                Cell: ({ value, row }) =>
                    row.isGrouped ? <div className="text-right">{formatValue(value, "double")}</div> : null,
                ...typeSpecificProps({ clrType: "double" }),
            },
            {
                Header: "Ожидаемые платежи",
                accessor: "pendingPaymentsByDate",
                Aggregated: ({ value, row }) => {
                    if (row.isGrouped) {
                        value = row.subRows[0].original.pendingPaymentsByDate;
                    }
                    return (
                        <div className="text-right">
                            <b>{formatValue(value, "double")}</b>
                        </div>
                    );
                },
                Cell: ({ value, row }) =>
                    row.isGrouped ? <div className="text-right">{formatValue(value, "double")}</div> : null,
                ...typeSpecificProps({ clrType: "double" }),
            },
            {
                Header: this.isHiredCarSource() ? "Наёмный ТС" : "ТС",
                accessor: "carNumber",
                Cell: ({ value }) =>
                    value && (
                        <div className="text-center">
                            <span className="carnumber-simple">{value}</span>
                        </div>
                    ),
                ...typeSpecificProps({ clrType: "string" }),
                filter: (rows, _id, filterValue) => {
                    if (!filterValue || !filterValue?.value) {
                        return rows;
                    }
                    return rows.filter((row) => row.values.carId === filterValue.value);
                },
                ...(this.isMixedSource()
                    ? {}
                    : {
                          Filter: reportFilterWrapper((props) => (
                              <SelectColumnFilter
                                  {...props}
                                  selectProps={{
                                      url: this.isHiredCarSource() ? hiredCarsListForSelect : numbersListWithDeleted,
                                      inputProps: { name: "carId" },
                                      propName: "number",
                                  }}
                              />
                          )),
                      }),
            },
            {
                Header: "Маршрут",
                accessor: "route",
                Cell: ({ value }) => <div className="text-wrap-auto two-line-ellipsis">{value}</div>,
                ...typeSpecificProps({ clrType: "string" }),
            },
            ...(this.isHiredCarSource() || this.isMixedSource()
                ? [
                      {
                          Header: "Дата заказа",
                          accessor: "orderDate",
                          Cell: ({ value }) => (
                              <div className="text-center">{value ? moment(value).format("DD.MM.YYYY") : ""}</div>
                          ),
                          ...typeSpecificProps({ clrType: "dateonly" }),
                      },
                  ]
                : []),
            {
                Header: this.props.isAccountsPayable ? "Дата получения счёта" : "Дата выстав. счёта",
                accessor: "invoiceDate",
                Cell: ({ row }) =>
                    !row.isGrouped && (
                        <WithChangeDate
                            name="invoiceDate"
                            payment={row.original}
                            isAccountsPayable={this.props.isAccountsPayable}
                            updateData={(newItem) => this.updateData(newItem, row.index)}
                        />
                    ),
                ...typeSpecificProps({ clrType: "dateonly" }),
            },
            {
                Header: this.props.isAccountsPayable ? "План. дата выплаты" : "План. дата оплаты",
                accessor: "planPaymentDate",
                Cell: ({ value, row }) =>
                    row.isGrouped ? (
                        <div className="text-center">{planPaymentDateGroupLabel(value)}</div>
                    ) : (
                        <WithChangeDate
                            name="planPaymentDate"
                            payment={row.original}
                            isAccountsPayable={this.props.isAccountsPayable}
                            updateData={(newItem) => this.updateData(newItem, row.index)}
                        />
                    ),
                ...typeSpecificProps({ clrType: "dateonly" }),
            },
            {
                Header: this.props.isAccountsPayable ? "Факт. дата выплаты" : "Факт. дата оплаты",
                accessor: "factPaymentDate",
                Cell: ({ row }) =>
                    !row.isGrouped && (
                        <WithChangeDate
                            name="factPaymentDate"
                            payment={row.original}
                            isAccountsPayable={this.props.isAccountsPayable}
                            updateData={(newItem) => this.updateData(newItem, row.index)}
                        />
                    ),
                ...typeSpecificProps({ clrType: "dateonly" }),
            },
            {
                Header: this.props.isAccountsPayable ? "Статус выплаты" : "Статус оплаты",
                accessor: "bidPaymentStatus",
                Cell: ({ value, row: { values, original, isGrouped, index } }) =>
                    !isGrouped && (
                        <WithChangeStatus
                            payment={original}
                            isAccountsPayable={this.props.isAccountsPayable}
                            updateData={(newItem) => this.updateData(newItem, index)}
                        >
                            <div className="text-center">
                                <div className={cn("badge badge-pill", this.paymentStatusClassName(original))}>
                                    {enumLabel(this.paymentStatuses(), value)}
                                </div>
                                {values.overdueDays > 0 && (
                                    <div className="small">
                                        Просрочено: {values.overdueDays}{" "}
                                        {ending(values.overdueDays, "день", "дня", "дней")}
                                    </div>
                                )}
                                {value === bidPaymentStatusesEnum.PartiallyPaid && (
                                    <div className="small">
                                        <span className="pr-1 text-nowrap">
                                            {this.props.isAccountsPayable ? "Остаток выплаты:" : "Остаток оплаты:"}
                                        </span>
                                        <span className="text-nowrap">
                                            {formatNumber(original.remainingPayment ?? 0, {
                                                style: "currency",
                                                currency: this.props.organization?.currency,
                                            })}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </WithChangeStatus>
                    ),
                filter: (rows, _id, filterValue) => {
                    if (!filterValue || !filterValue?.value) {
                        return rows;
                    }
                    const values = Array.isArray(filterValue.value) ? filterValue.value : [filterValue.value];
                    if (values.length === 0) {
                        return rows;
                    }
                    return rows.filter((row) => values.includes(row.values.bidPaymentStatus));
                },
                Filter: reportFilterWrapper((props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            ...defaultMultipleSelectProps,
                            options: this.paymentStatuses(),
                            inputProps: { name: "bidPaymentStatus" },
                            components: { ValueContainer },
                        }}
                    />
                )),
            },
            {
                Header: this.props.isAccountsPayable ? "Остаток к выплате" : "Остаток к оплате",
                accessor: "remainingPayment",
                Cell: ({ value, row }) =>
                    !row.isGrouped && row.original.bidPaymentStatus === bidPaymentStatusesEnum.PartiallyPaid ? (
                        <div className="text-right">{formatValue(value, "double")}</div>
                    ) : null,
                ...typeSpecificProps({ clrType: "double" }),
            },
            {
                Header: "Комментарий",
                accessor: "comment",
                Cell: ({ value, row }) =>
                    !row.isGrouped && (
                        <Tooltip overlay={value} overlayClassName="tooltip--sm">
                            <div>
                                <WithChangeDate
                                    name="comment"
                                    payment={row.original}
                                    isAccountsPayable={this.props.isAccountsPayable}
                                    updateData={(newItem) => this.updateData(newItem, row.index)}
                                />
                            </div>
                        </Tooltip>
                    ),
                ...typeSpecificProps({ clrType: "string" }),
            },
            { accessor: "counterpartyId" },
            { accessor: "carId" },
            { accessor: "legalPersonId" },
            { accessor: "bidPaymentStatusReadable" },
            { accessor: "overdueDays" },
            { accessor: "bidsCount" },
            { accessor: "planIncome" },
        ];
    };

    tableExtraProps = () => ({
        autoResetFilters: false,
        initialState: {
            hiddenColumns: [
                "counterpartyId",
                "carId",
                "legalPersonId",
                "bidPaymentStatusReadable",
                "overdueDays",
                "bidsCount",
                "planIncome",
                ...(this.props.groupByDates ? [] : ["receivedAmountByDate", "pendingPaymentsByDate"]),
            ],
            groupBy: this.props.groupByDates ? ["planPaymentDate"] : [],
        },
        data: this.state.data,
        onFilteredRowsChange: this.setVisibleRows,
    });

    counterpartyInfo = () => {
        const counterpartyFilter = this.state.filters.find((filter) => filter.id === "counterpartyName");
        if (!counterpartyFilter?.value?.option) {
            return null;
        }

        const { id, name } = counterpartyFilter.value.option;

        return <CounterpartyInfo id={id} name={name} modal={this.props.modal} organization={this.props.organization} />;
    };

    footerAdditionalInfo = () => {
        const { data } = this.props;
        if (!data.footerAdditionalInfo) {
            return null;
        }
        const rows = this.state.visibleRows || this.state.data || [];
        const unpaidAmount = (row) =>
            row.bidPaymentStatus === bidPaymentStatusesEnum.Paid ? 0 : (row.remainingPayment ?? row.price ?? 0);
        const paidAmount = (row) => {
            if (row.bidPaymentStatus === bidPaymentStatusesEnum.Paid) {
                return row.price || 0;
            }
            if (row.bidPaymentStatus === bidPaymentStatusesEnum.PartiallyPaid) {
                return Math.max((row.price || 0) - (row.remainingPayment || 0), 0);
            }
            return 0;
        };
        const totals = this.props.isAccountsPayable
            ? {
                  plannedPayments: rows.reduce((sum, row) => sum + (row.price || 0), 0),
                  paidAmount: rows.reduce((sum, row) => sum + paidAmount(row), 0),
                  pendingInvoicedPayments: rows
                      .filter((row) => row.invoiceDate)
                      .reduce((sum, row) => sum + unpaidAmount(row), 0),
                  overdueDebt: rows
                      .filter((row) => row.isPaymentOverdue || row.overdueDays > 1)
                      .reduce((sum, row) => sum + unpaidAmount(row), 0),
              }
            : {
                  pendingPayments: rows.reduce((sum, row) => sum + unpaidAmount(row), 0),
                  receivedAmount: rows.reduce((sum, row) => sum + paidAmount(row), 0),
                  totalDebt: rows
                      .filter((row) => row.isPaymentOverdue || row.overdueDays > 1)
                      .reduce((sum, row) => sum + unpaidAmount(row), 0),
              };
        const footerColumns = this.props.isAccountsPayable
            ? [
                  { label: "Планируется к оплате", propertyName: "PlannedPayments", clrType: "double" },
                  { label: "Оплачено", propertyName: "PaidAmount", clrType: "double" },
                  { label: "Ожидает оплаты", propertyName: "PendingInvoicedPayments", clrType: "double" },
                  { label: "Просроченная кредиторская задолженность", propertyName: "OverdueDebt", clrType: "double" },
              ]
            : [
                  { label: "Ожидается к оплате", propertyName: "PendingPayments", clrType: "double" },
                  { label: "Получено", propertyName: "ReceivedAmount", clrType: "double" },
                  { label: "Просроченная дебиторская задолженность", propertyName: "TotalDebt", clrType: "double" },
              ];
        return (
            <div className="card card-body flex-grow-0 px-0 py-1 mt-3 w-fit-content">
                <table className="table table-xs mb-0">
                    <tbody>
                        <tr>
                            <td className="noborder font-weight-bold">Итого</td>
                            <td className="noborder" />
                        </tr>
                        {footerColumns.map(({ label, propertyName, clrType }) => {
                            const property = columnName({ propertyName });
                            const value = formatValue(
                                totals[property] ?? data.footerAdditionalInfo.data[property],
                                clrType,
                            );
                            return (
                                <tr key={propertyName}>
                                    <td className="font-weight-bold">{label}</td>
                                    <td className="text-right">{value}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    underTableData = () => (
        <div className="d-flex align-items-start">
            {this.footerAdditionalInfo()}
            {this.counterpartyInfo()}
        </div>
    );
}
